import { lookupTranslation } from '../i18n/translations';

const cyrToLatMap: { [key: string]: string } = {
  'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Ђ': 'Đ', 'Е': 'E', 'Ж': 'Ž', 'З': 'Z', 'И': 'I',
  'Ј': 'J', 'К': 'K', 'Л': 'L', 'Љ': 'Lj', 'М': 'M', 'Н': 'N', 'Њ': 'Nj', 'О': 'O', 'П': 'P', 'Р': 'R',
  'С': 'S', 'Т': 'T', 'Ћ': 'Ć', 'У': 'U', 'Ф': 'F', 'Х': 'H', 'Ц': 'C', 'Ч': 'Č', 'Џ': 'Dž', 'Ш': 'Š',
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'ђ': 'đ', 'е': 'e', 'ж': 'ž', 'з': 'z', 'и': 'i',
  'ј': 'j', 'к': 'k', 'л': 'l', 'љ': 'lj', 'м': 'm', 'н': 'n', 'њ': 'nj', 'о': 'o', 'п': 'p', 'р': 'r',
  'с': 's', 'т': 't', 'ћ': 'ć', 'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'č', 'џ': 'dž', 'ш': 'š'
};

export type Script = 'cyr' | 'lat' | 'en' | 'de' | 'fr' | 'it' | 'el';

/** Convert Cyrillic to Latin (character-level transliteration). */
const toLatin = (text: string): string =>
  text.split('').map(char => cyrToLatMap[char] || char).join('');

/**
 * Synchronous translation/transliteration.
 * - cyr: returns as-is
 * - lat: character-map transliteration
 * - en/de/fr/it/el: dictionary lookup → falls back to Latin transliteration
 */
export const transliterate = (text: string, toScript: Script): string => {
  if (toScript === 'cyr') return text;
  if (toScript === 'lat') return toLatin(text);
  // Foreign language: try dictionary first, fall back to latin
  return lookupTranslation(text, toScript) || toLatin(text);
};

/**
 * Async translation for a single text (kept for backward compatibility).
 * 1. Check dictionary (instant)
 * 2. Check localStorage cache (instant)
 * 3. Call backend proxy → DeepL API → cache result
 */
export const translateAsync = async (text: string, lang: Script): Promise<string> => {
  if (!text || !text.trim()) return text;
  if (lang === 'cyr') return text;
  if (lang === 'lat') return toLatin(text);

  // 1. Check dictionary
  const dict = lookupTranslation(text, lang);
  if (dict) return dict;

  // 2. Check localStorage cache
  const cacheKey = `tr:${lang}:${text}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch {
    // localStorage unavailable — continue to API
  }

  // 3. Call backend proxy → DeepL
  try {
    const res = await fetch('/api/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, lang }),
    });
    if (!res.ok) return toLatin(text);
    const data = await res.json();
    const translated = data.translated || toLatin(text);

    // Cache the result
    try {
      localStorage.setItem(cacheKey, translated);
    } catch {
      // localStorage full — ignore
    }

    return translated;
  } catch {
    // Network error — return latin transliteration as fallback
    return toLatin(text);
  }
};

/**
 * Batch async translation — sends multiple texts in a single API call.
 * For each text:
 *   1. Check dictionary (instant)
 *   2. Check localStorage cache (instant)
 *   3. Texts not found above are sent together to the backend batch endpoint
 * Returns a Map<text, translation>.
 */
export const translateBatchAsync = async (
  texts: string[],
  lang: Script,
): Promise<Map<string, string>> => {
  const results = new Map<string, string>();
  if (lang === 'cyr') {
    for (const t of texts) results.set(t, t);
    return results;
  }
  if (lang === 'lat') {
    for (const t of texts) results.set(t, toLatin(t));
    return results;
  }

  // Resolve what we can locally
  const needApi: string[] = [];
  for (const text of texts) {
    if (!text || !text.trim()) {
      results.set(text, text);
      continue;
    }

    // Dictionary
    const dict = lookupTranslation(text, lang);
    if (dict) {
      results.set(text, dict);
      continue;
    }

    // localStorage
    try {
      const cached = localStorage.getItem(`tr:${lang}:${text}`);
      if (cached) {
        results.set(text, cached);
        continue;
      }
    } catch {
      // ignore
    }

    needApi.push(text);
  }

  // If everything resolved locally, return immediately
  if (needApi.length === 0) return results;

  // Call backend batch endpoint
  try {
    const res = await fetch('/api/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ texts: needApi, lang }),
    });

    if (res.ok) {
      const data = await res.json();
      const translations: string[] = data.translations || [];
      for (let i = 0; i < needApi.length; i++) {
        const translated = translations[i] || toLatin(needApi[i]);
        results.set(needApi[i], translated);
        // Cache each result in localStorage
        try {
          localStorage.setItem(`tr:${lang}:${needApi[i]}`, translated);
        } catch {
          // localStorage full — ignore
        }
      }
    } else {
      // API error — fall back to latin for all
      for (const text of needApi) {
        results.set(text, toLatin(text));
      }
    }
  } catch {
    // Network error — fall back to latin for all
    for (const text of needApi) {
      results.set(text, toLatin(text));
    }
  }

  return results;
};
